// SPDX-FileCopyrightText: © 2025 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from '@stencil/core'
import {
  AttachInternals,
  Component,
  Element,
  Event,
  h,
  Listen,
  Prop,
  readTask,
  State,
  Watch,
} from '@stencil/core'

import type { QdsFocusEventDetail } from '../../helpers'
import {
  invariant,
  isOverflowing,
  pickFocusEventAttributes,
} from '../../helpers'
import type { Size } from '../shared'

export type Type = 'checkbox' | undefined

/**
 *
 *
 * @see https://quartz.se.com/build/components/menu-item
 */
@Component({
  tag: 'qds-menu-item',
  shadow: true,
  styleUrl: 'menu-item.css',
})
export class MenuItem implements ComponentInterface {
  /**
   * Prevents the menu item from being interacted with: it cannot be clicked
   * or focused.
   */
  @Prop() public readonly disabled?: boolean = false

  /**
   * The name of the icon to render.
   *
   * Available names depend on the icon library being used.
   */
  @Prop() public readonly iconName?: string

  /**
   * The name of a registered icon library.
   */
  @Prop() public readonly iconLibrary: string = 'default'

  /**
   * The menu item's size.
   */
  @Prop() public readonly size?: Size = 'standard'

  /**
   * Whether or not the menu item should be selectable.
   */
  @Prop() public type: Type

  /**
   * Whether or not the menu item is checked.
   */
  @Prop({ mutable: true }) public checked?: boolean

  /**
   * The menu item's text
   */
  @Prop() public readonly text!: string

  /**
   * The shortcut of the action.
   */
  @Prop() public readonly shortcut?: string

  @State() private hasSubmenuSlot = false

  @State() private tooltip = false

  /**
   * Emitted when the menu item loses focus.
   */
  @Event({ eventName: 'qdsBlur', bubbles: false, cancelable: false })
  private readonly blurEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when the menu item gains focus.
   */
  @Event({ eventName: 'qdsFocus', bubbles: false, cancelable: false })
  private readonly focusEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when the menu item is checked.
   */
  @Event({ eventName: 'qdsSelect', cancelable: false })
  private readonly selectEmitter!: EventEmitter<void>

  @Element() private readonly host!: HTMLElement

  @AttachInternals() private readonly internals!: ElementInternals

  #ro?: ResizeObserver

  #menuItem!: HTMLDivElement

  #span?: HTMLSpanElement

  get #computedSize(): Size {
    switch (this.size) {
      case 'standard':
      case 'small':
      case 'large': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  get #computedType(): Type {
    return this.type ?? undefined
  }

  get #computedCheckable(): boolean {
    return this.#computedType === 'checkbox'
  }

  get #computedChecked(): boolean {
    return (
      (this.#computedCheckable && !this.#hasSubmenu && this.checked) ?? false
    )
  }

  get #computedDisabled(): boolean {
    return this.disabled ?? false
  }

  get #hasSubmenu(): boolean {
    return this.hasSubmenuSlot
  }

  @Listen('blur')
  protected onBlur(event: FocusEvent): void {
    this.blurEmitter.emit(pickFocusEventAttributes(event))
  }

  @Listen('focus')
  protected onFocus(event: FocusEvent): void {
    this.focusEmitter.emit(pickFocusEventAttributes(event))
  }

  @Listen('click')
  protected onClick(event: MouseEvent): void {
    if (this.#computedDisabled) {
      event.stopImmediatePropagation()
      return
    }

    if (this.#computedCheckable) {
      this.checked = !this.#computedChecked
      this.selectEmitter.emit()
    }
  }

  @Watch('disabled')
  protected disabledChanged(): void {
    this.internals.ariaDisabled = this.#computedDisabled.toString()
  }

  @Watch('checked')
  protected checkedChanged(): void {
    this.internals.ariaChecked = this.#computedChecked.toString()
  }

  public componentDidLoad(): void {
    this.checkedChanged()
    invariant(this.#span)

    const span = this.#span
    readTask(() => {
      this.tooltip = isOverflowing(span)
    })
    this.#ro = new ResizeObserver(([spanEntry]) => {
      this.tooltip = isOverflowing(spanEntry.target)
    })
    this.#ro.observe(this.#span)
  }

  public componentWillLoad(): void {
    this.hasSubmenuSlot = this.host.hasChildNodes()
    this.internals.role =
      this.type === 'checkbox' ? 'menuitemcheckbox' : 'menuitem'
    this.disabledChanged()
  }

  public render() {
    return (
      <div
        class={{
          'qds-menu-item': true,
          'qds-disabled': this.#computedDisabled,
          'qds-has-submenu': this.#hasSubmenu,
        }}
        ref={this.#menuitemDivRef}
        data-size={this.#computedSize}
        aria-hidden="true"
      >
        <div class="qds-menu-item-container">
          {this.#computedCheckable && !this.#computedChecked && (
            <span class="menu-item-checkable" />
          )}
          {this.#computedChecked && (
            <qds-icon class="qds-icon" library="core" name="checked" />
          )}
          {this.iconName !== undefined && this.iconName !== '' && (
            <qds-icon
              class="qds-icon"
              library={this.iconLibrary}
              name={this.iconName}
            />
          )}
          <span class="qds-text" ref={this.#spanRef}>
            {this.text}
          </span>
          {this.shortcut !== undefined && this.shortcut !== '' && (
            <span class="qds-shortcut">{this.shortcut}</span>
          )}
          {this.#hasSubmenu && (
            <qds-icon class="qds-icon" library="core" name="navigation" />
          )}
        </div>
        {this.#hasSubmenu && (
          <qds-dropdown
            class="qds-accessory qds-related"
            placement="right-start"
            target={this.#menuitemDivRef}
          >
            <slot />
          </qds-dropdown>
        )}
        {this.tooltip && (
          <qds-tooltip ref={this.#tooltipRef}>{this.text}</qds-tooltip>
        )}
      </div>
    )
  }

  readonly #menuitemDivRef = (menuItem?: HTMLDivElement): HTMLDivElement => {
    if (menuItem) {
      this.#menuItem = menuItem
    }

    return this.#menuItem
  }

  readonly #spanRef = (span?: HTMLSpanElement): void => {
    this.#span = span
  }

  readonly #tooltipRef = (tooltip?: HTMLQdsTooltipElement): void => {
    if (tooltip)
      // eslint-disable-next-line no-param-reassign
      tooltip.target = this.#menuItem
  }
}
